/**
 * Contract Drafting Detail - Redux Slice
 * Using Redux Toolkit pattern with manual thunk actions
 */

import { ContractDraftingDetailState } from "../types";

/**
 * Action Type Constants
 */
export const CONTRACT_DRAFTING_DETAIL_ACTIONS = {
  // Data Loading
  FETCH_REQUEST_START: "CONTRACT_DRAFTING_DETAIL/FETCH_REQUEST_START",
  FETCH_REQUEST_SUCCESS: "CONTRACT_DRAFTING_DETAIL/FETCH_REQUEST_SUCCESS",
  FETCH_REQUEST_ERROR: "CONTRACT_DRAFTING_DETAIL/FETCH_REQUEST_ERROR",

  FETCH_DATAPOINTS_SUCCESS: "CONTRACT_DRAFTING_DETAIL/FETCH_DATAPOINTS_SUCCESS",
  FETCH_DOCUMENTS_SUCCESS: "CONTRACT_DRAFTING_DETAIL/FETCH_DOCUMENTS_SUCCESS",
  FETCH_DRAFTS_SUCCESS: "CONTRACT_DRAFTING_DETAIL/FETCH_DRAFTS_SUCCESS",
  FETCH_TEMPLATE_SUCCESS: "CONTRACT_DRAFTING_DETAIL/FETCH_TEMPLATE_SUCCESS",

  // Edit Mode
  ENTER_EDIT_MODE: "CONTRACT_DRAFTING_DETAIL/ENTER_EDIT_MODE",
  EXIT_EDIT_MODE: "CONTRACT_DRAFTING_DETAIL/EXIT_EDIT_MODE",
  UPDATE_FIELD_VALUE: "CONTRACT_DRAFTING_DETAIL/UPDATE_FIELD_VALUE",

  // Saving
  SAVE_CHANGES_START: "CONTRACT_DRAFTING_DETAIL/SAVE_CHANGES_START",
  SAVE_CHANGES_SUCCESS: "CONTRACT_DRAFTING_DETAIL/SAVE_CHANGES_SUCCESS",
  SAVE_CHANGES_ERROR: "CONTRACT_DRAFTING_DETAIL/SAVE_CHANGES_ERROR",

  // Document Operations
  UPLOAD_START: "CONTRACT_DRAFTING_DETAIL/UPLOAD_START",
  UPLOAD_SUCCESS: "CONTRACT_DRAFTING_DETAIL/UPLOAD_SUCCESS",
  UPLOAD_ERROR: "CONTRACT_DRAFTING_DETAIL/UPLOAD_ERROR",
  DELETE_DOCUMENT_SUCCESS: "CONTRACT_DRAFTING_DETAIL/DELETE_DOCUMENT_SUCCESS",
  SUBMIT_DOCUMENTS_START: "CONTRACT_DRAFTING_DETAIL/SUBMIT_DOCUMENTS_START",
  SUBMIT_DOCUMENTS_SUCCESS: "CONTRACT_DRAFTING_DETAIL/SUBMIT_DOCUMENTS_SUCCESS",
  SUBMIT_DOCUMENTS_ERROR: "CONTRACT_DRAFTING_DETAIL/SUBMIT_DOCUMENTS_ERROR",
  CLEAR_NEW_DOCUMENTS: "CONTRACT_DRAFTING_DETAIL/CLEAR_NEW_DOCUMENTS",

  // Draft Generation
  GENERATE_DRAFT_START: "CONTRACT_DRAFTING_DETAIL/GENERATE_DRAFT_START",
  GENERATE_DRAFT_SUCCESS: "CONTRACT_DRAFTING_DETAIL/GENERATE_DRAFT_SUCCESS",
  GENERATE_DRAFT_ERROR: "CONTRACT_DRAFTING_DETAIL/GENERATE_DRAFT_ERROR",
  UPDATE_GENERATION_STATUS: "CONTRACT_DRAFTING_DETAIL/UPDATE_GENERATION_STATUS",

  // Workflow Actions
  REQUEST_REVIEW_SUCCESS: "CONTRACT_DRAFTING_DETAIL/REQUEST_REVIEW_SUCCESS",
  MARK_COMPLETE_SUCCESS: "CONTRACT_DRAFTING_DETAIL/MARK_COMPLETE_SUCCESS",

  // Modal Control
  OPEN_MODAL: "CONTRACT_DRAFTING_DETAIL/OPEN_MODAL",
  CLOSE_MODAL: "CONTRACT_DRAFTING_DETAIL/CLOSE_MODAL",

  // Error Handling
  CLEAR_ERROR: "CONTRACT_DRAFTING_DETAIL/CLEAR_ERROR",
};

/**
 * Initial State
 */
const initialState: ContractDraftingDetailState = {
  request: null,
  datapoints: [],
  documents: [],
  drafts: [],
  template: null,

  isLoading: false,
  isError: false,
  errorMessage: null,
  isEditMode: false,
  isSaving: false,
  isUploading: false,
  isSubmittingDocuments: false,
  newlyUploadedDocumentIds: [],

  modals: {
    deleteDocument: {
      visible: false,
      documentId: null,
    },
    previewTemplate: {
      visible: false,
    },
    viewDraft: {
      visible: false,
      data: {
        draft_url: null,
        draft_content: "",
        generated_at: null,
        generated_by: null,
        size_kb: null,
      },
    },
    viewRedlining: {
      visible: false,
      data: {
        draft_url: null,
        review_comments: [],
        review_status: "",
      },
    },
    viewFinalDraft: {
      visible: false,
      data: {
        reviewed_draft_id: null,
        generated_at: null,
        version_number: null,
      },
    },
    generateDraft: {
      visible: false,
    },
    requestReview: {
      visible: false,
    },
    markComplete: {
      visible: false,
    },
    validationError: {
      visible: false,
      missingFields: [],
    },
  },

  editedDatapoints: {},
  generationTaskId: null,
  generationStatus: "idle",
};

/**
 * Reducer
 */
const contractDraftingDetailReducer = (
  state = initialState,
  action: any
): ContractDraftingDetailState => {
  switch (action.type) {
    // Fetch Request
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_REQUEST_START:
      return {
        ...state,
        isLoading: true,
        isError: false,
        errorMessage: null,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_REQUEST_SUCCESS:
      return {
        ...state,
        request: action.payload,
        isLoading: false,
        // Sync the optimistic generationStatus flag with the authoritative
        // backend status. Without this the flag gets stuck at "pending" after
        // async draft completion or when navigating between contracts, which
        // locks the Generate Draft modal (isDraftingInProgress stays true).
        generationStatus:
          action.payload?.status === 'DRAFTING_IN_PROGRESS' ? 'pending' : 'idle',
        // Clear newly uploaded documents if request is being processed
        newlyUploadedDocumentIds:
          action.payload?.status === 'INTAKE_IN_PROGRESS'
            ? []
            : state.newlyUploadedDocumentIds,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_REQUEST_ERROR:
      return {
        ...state,
        isLoading: false,
        isError: true,
        errorMessage: action.payload,
      };

    // Fetch Datapoints
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_DATAPOINTS_SUCCESS:
      return {
        ...state,
        datapoints: action.payload,
      };

    // Fetch Documents
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_DOCUMENTS_SUCCESS:
      return {
        ...state,
        documents: action.payload,
      };

    // Fetch Drafts
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_DRAFTS_SUCCESS: {
      return {
        ...state,
        drafts: action.payload,
      };
    }

    // Fetch Template
    // API returns { template: {...}, merge_fields: {...} } — store only the inner template object
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.FETCH_TEMPLATE_SUCCESS:
      return {
        ...state,
        template: action.payload != null && 'template' in action.payload ? action.payload.template : action.payload,
      };

    // Edit Mode
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.ENTER_EDIT_MODE:
      return {
        ...state,
        isEditMode: true,
        editedDatapoints: {},
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.EXIT_EDIT_MODE:
      return {
        ...state,
        isEditMode: false,
        editedDatapoints: {},
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.UPDATE_FIELD_VALUE:
      const newEditedDatapoints = {
        ...state.editedDatapoints,
        [action.payload.fieldId]: action.payload.value,
      };
      return {
        ...state,
        editedDatapoints: newEditedDatapoints,
      };

    // Saving
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.SAVE_CHANGES_START:
      return {
        ...state,
        isSaving: true,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.SAVE_CHANGES_SUCCESS:
      const savedUpdates = action.payload?.updates || [];
      const savedFieldIds = savedUpdates.map((u: any) => u.field_id);
      
      return {
        ...state,
        isSaving: false,
        isEditMode: false,
        // Clear only the saved fields from editedDatapoints
        editedDatapoints: Object.fromEntries(
          Object.entries(state.editedDatapoints).filter(
            ([id]) => !savedFieldIds.includes(id)
          )
        ),
        datapoints: state.datapoints.map((dp) => {
          const update = savedUpdates.find((u: any) => u.field_id === dp.field);
          if (update) {
            return { 
              ...dp, 
              field_value: update.field_value,
              source_type: "MANUAL" // Mark as manual once saved
            };
          }
          return dp;
        }),
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.SAVE_CHANGES_ERROR:
      return {
        ...state,
        isSaving: false,
        isError: true,
        errorMessage: action.payload,
      };

    // Document Upload
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.UPLOAD_START:
      return {
        ...state,
        isUploading: true,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.UPLOAD_SUCCESS:
      const newDocs = action.payload || [];
      const newDocIds = newDocs.map((doc: any) => doc.document_id).filter(Boolean);
      
      return {
        ...state,
        isUploading: false,
        documents: [...state.documents, ...newDocs],
        newlyUploadedDocumentIds: [...state.newlyUploadedDocumentIds, ...newDocIds],
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.UPLOAD_ERROR:
      return {
        ...state,
        isUploading: false,
        isError: true,
        errorMessage: action.payload,
      };

    // Delete Document
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.DELETE_DOCUMENT_SUCCESS:
      const deletedDocId = action.payload;
      return {
        ...state,
        documents: state.documents.filter((doc: any) => doc.document_id !== deletedDocId),
        // Remove from newly uploaded list if it was there
        newlyUploadedDocumentIds: state.newlyUploadedDocumentIds.filter(id => id !== deletedDocId),
        // Close the delete confirmation modal
        modals: {
          ...state.modals,
          deleteDocument: {
            visible: false,
            documentId: null,
          },
        },
      };
    
    // Submit Documents for Processing
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.SUBMIT_DOCUMENTS_START:
      return {
        ...state,
        isSubmittingDocuments: true,
      };
    
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.SUBMIT_DOCUMENTS_SUCCESS:
      return {
        ...state,
        isSubmittingDocuments: false,
        newlyUploadedDocumentIds: [], // Clear after successful submission
        // Update request status if provided in payload and request exists
        request: (action.payload?.status && state.request)
          ? { ...state.request, status: action.payload.status } as typeof state.request
          : state.request,
      };
    
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.SUBMIT_DOCUMENTS_ERROR:
      return {
        ...state,
        isSubmittingDocuments: false,
        isError: true,
        errorMessage: action.payload,
        // Don't clear newlyUploadedDocumentIds on error - user might want to retry
      };
    
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.CLEAR_NEW_DOCUMENTS:
      return {
        ...state,
        newlyUploadedDocumentIds: [],
      };

    // Draft Generation
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.GENERATE_DRAFT_START:
      return {
        ...state,
        generationStatus: "pending",
        request: state.request
          ? { ...state.request, status: "DRAFTING_IN_PROGRESS" }
          : null,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.GENERATE_DRAFT_SUCCESS:
      return {
        ...state,
        generationStatus: "success",
        drafts: [...state.drafts, action.payload],
        request: state.request
          ? { ...state.request, status: "DRAFT_GENERATED" }
          : null,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.GENERATE_DRAFT_ERROR:
      return {
        ...state,
        generationStatus: "error",
        isError: true,
        errorMessage: action.payload,
        request: state.request
          ? { ...state.request, status: "READY_FOR_DRAFTING" }
          : null,
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.UPDATE_GENERATION_STATUS:
      return {
        ...state,
        generationTaskId: action.payload.taskId,
        generationStatus: action.payload.status,
      };

    // Workflow Actions
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.REQUEST_REVIEW_SUCCESS:
      return {
        ...state,
        request: state.request
          ? { ...state.request, status: "REVIEWED" }
          : null,
        modals: {
          ...state.modals,
          requestReview: {
            visible: false,
          },
        },
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.MARK_COMPLETE_SUCCESS:
      return {
        ...state,
        request: state.request
          ? { ...state.request, status: "COMPLETED" }
          : null,
        modals: {
          ...state.modals,
          markComplete: {
            visible: false,
          },
        },
      };

    // Modal Control
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.OPEN_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload.modalName]: {
            ...state.modals[action.payload.modalName as keyof typeof state.modals],
            visible: true,
            ...(action.payload.data || {}),
          },
        },
      };

    case CONTRACT_DRAFTING_DETAIL_ACTIONS.CLOSE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: {
            ...initialState.modals[action.payload as keyof typeof initialState.modals],
            visible: false,
          },
        },
      };

    // Error Handling
    case CONTRACT_DRAFTING_DETAIL_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        isError: false,
        errorMessage: null,
      };

    default:
      return state;
  }
};

export default contractDraftingDetailReducer;
